import { type PageCategory } from "./components/page-header";
import Analysis from "./pages/analysis";
import { BusinessMetricsPage } from "./pages/business";
import CalibrationPlotsPage from "./pages/calibration/plots";
import CalibrationAnalysis from "./pages/calibration/tables";
import CampaignFilter from "./pages/campaign-filter";
import Efficiency from "./pages/efficiency";
import EvidenceGatesPage from "./pages/evidence-gates";
import ExperimentsPage from "./pages/experiments";
import ExperimentsCampaignAssignmentPage from "./pages/experiments/campaign-assignment";
import CreateExperimentPage from "./pages/experiments/create";
import ExperimentsRuntimePage from "./pages/experiments/runtime";
import ExperimentsView from "./pages/experiments/view";
import JiraMetricsPage from "./pages/jira-metrics";
import Overview from "./pages/overview";
import Performance from "./pages/performance";
import TeamGoalsPage from "./pages/team-goals";

export type RouteHandle = {
  title: string;
  description: string;
  category: PageCategory;
  parent?: { title: string; path: string };
  skipHeader?: boolean;
};

export type AppRoute = { path: string; element: JSX.Element; handle: RouteHandle };

export const appRoutes: AppRoute[] = [
  {
    path: "/",
    element: <Overview />,
    handle: { title: "Overview", description: "Top level metrics for last month.", category: "clients" },
  },
  {
    path: "performance",
    element: <Performance />,
    handle: {
      title: "Performance",
      description: "Monitor key performance metrics and conversion analysis for your campaigns.",
      category: "clients",
    },
  },
  {
    path: "business",
    element: <BusinessMetricsPage />,
    handle: {
      title: "Business Metrics",
      description: "An overview of business health metrics grouped by strategic category.",
      category: "team",
    },
  },
  {
    path: "efficiency",
    element: <Efficiency />,
    handle: {
      title: "Bidding Efficiency",
      description: "Analyzes bidding efficiency of campaigns.",
      category: "product",
    },
  },
  {
    path: "calibration-analysis",
    element: <CalibrationAnalysis />,
    handle: {
      title: "Calibration Analysis",
      description: "Analyzes predicted and actual installs per cost. (AP Conversions / Revenue €)",
      category: "product",
    },
  },
  {
    path: "calibration-plots",
    element: <CalibrationPlotsPage />,
    handle: {
      title: "Calibration Plots",
      description:
        "Visualizes calibration metrics across volume quantile buckets based on bid volume, sorted by predicted install rate.",
      category: "product",
    },
  },
  {
    path: "analysis",
    element: <Analysis />,
    handle: { title: "Analysis", description: "Metrics cohorted by bid time.", category: "clients" },
  },
  {
    path: "campaign-filter",
    element: <CampaignFilter />,
    handle: {
      title: "Campaign Filter",
      description: "An experimental approach to organize campaign filter events in more meaningful ways.",
      category: "clients",
    },
  },
  {
    path: "experiments",
    element: <ExperimentsPage />,
    handle: { title: "Experiments", description: "Manage and review your saved experiments.", category: "product" },
  },
  {
    path: "experiments/runtime",
    element: <ExperimentsRuntimePage />,
    handle: {
      title: "Runtimes",
      description: "Track experiment runtime and learning-loop closure time for completed experiments.",
      category: "product",
      parent: { title: "Experiments", path: "/experiments" },
      skipHeader: true,
    },
  },
  {
    path: "experiments/campaign-assignment",
    element: <ExperimentsCampaignAssignmentPage />,
    handle: {
      title: "Campaign Assignment",
      description: "Track campaign experiment membership across the past year and upcoming months.",
      category: "product",
      parent: { title: "Experiments", path: "/experiments" },
    },
  },
  {
    path: "experiments/create",
    element: <CreateExperimentPage />,
    handle: {
      title: "Create Experiment",
      description: "Set up a new campaign comparison experiment.",
      category: "product",
      parent: { title: "Experiments", path: "/experiments" },
    },
  },
  {
    path: "experiments/:id",
    element: <ExperimentsView />,
    handle: {
      title: "View Experiment",
      description: "Review detailed metrics for your experiment.",
      category: "product",
      parent: { title: "Experiments", path: "/experiments" },
      skipHeader: true,
    },
  },
  {
    path: "jira-metrics",
    element: <JiraMetricsPage />,
    handle: {
      title: "Jira Metrics",
      description: "Track Jira delivery metrics, cycle-time distributions, and sync health.",
      category: "team",
    },
  },
  {
    path: "evidence-gates",
    element: <EvidenceGatesPage />,
    handle: {
      title: "Evidence Gates",
      description: "Track the Evidence Gate 2026 criteria against experiment records.",
      category: "team",
    },
  },
  {
    path: "team-goals",
    element: <TeamGoalsPage />,
    handle: { title: "Team Goals", description: "Track progress on the 3 team goals for 2026.", category: "team" },
  },
];
